import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Stack, IconButton, Tooltip, Paper, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { shortAbsenceService } from '../../services/api';

const WORK_START = 9;
const WORK_END = 18;
const HOURS = Array.from({ length: WORK_END - WORK_START }, (_, i) => WORK_START + i);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 5 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
}

function formatDate(d) { return d.toISOString().slice(0, 10); }
function formatShortDate(d) { return `${d.getDate()}/${d.getMonth() + 1}`; }

export default function ShortAbsenceMap({ departmentId, refreshTrigger }) {
  const theme = useTheme();
  const [weekOffset, setWeekOffset] = useState(0);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  const baseDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const year = weekDates[0].getFullYear();
        const month = weekDates[0].getMonth() + 1;
        const data = await shortAbsenceService.getAll({ year, month, departmentId, status: undefined });
        setAbsences(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load absence map', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [weekDates, departmentId, refreshTrigger]);

  // Group absences by employee and date
  const employeeMap = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      const empKey = a.employeeId;
      if (!map[empKey]) {
        map[empKey] = { name: a.employeeName, absences: {} };
      }
      const dateKey = a.date;
      if (!map[empKey].absences[dateKey]) {
        map[empKey].absences[dateKey] = [];
      }
      map[empKey].absences[dateKey].push(a);
    });
    return map;
  }, [absences]);

  const employees = Object.entries(employeeMap).sort((a, b) => a[1].name.localeCompare(b[1].name));

  const getSlotColor = (status) => {
    switch (status) {
      case 0: case 'Pending': return alpha(theme.palette.warning.main, 0.5);
      case 1: case 'Approved': return alpha(theme.palette.success.main, 0.5);
      case 2: case 'Rejected': return alpha(theme.palette.error.main, 0.3);
      default: return 'transparent';
    }
  };

  const getAbsenceForSlot = (empAbsences, dateStr, hour) => {
    const dayAbsences = empAbsences[dateStr] || [];
    return dayAbsences.find(a => {
      const [sh] = a.startTime.split(':').map(Number);
      const [eh] = a.endTime.split(':').map(Number);
      return hour >= sh && hour < eh;
    });
  };

  const today = formatDate(new Date());

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Team Absence Map</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={() => setWeekOffset(w => w - 1)}><ChevronLeftIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(0)}><TodayIcon /></IconButton>
        <IconButton size="small" onClick={() => setWeekOffset(w => w + 1)}><ChevronRightIcon /></IconButton>
        <Typography variant="body2">
          {formatShortDate(weekDates[0])} - {formatShortDate(weekDates[4])}
        </Typography>
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, fontSize: 12 }}>
        <Chip label="Approved" size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.5) }} />
        <Chip label="Pending" size="small" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.5) }} />
        <Chip label="Rejected" size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.3) }} />
      </Stack>

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : employees.length === 0 ? (
        <Typography color="text.secondary">No short absences this week</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: `150px repeat(${5 * HOURS.length}, 20px)`, gap: '1px', minWidth: 150 + 5 * HOURS.length * 21 }}>
            {/* Header row */}
            <Box sx={{ fontWeight: 'bold', p: 0.5 }}>Employee</Box>
            {weekDates.map((d, di) => (
              HOURS.map((h, hi) => (
                <Tooltip key={`h-${di}-${hi}`} title={`${WEEKDAYS[di]} ${d.getDate()} - ${h}:00`}>
                  <Box sx={{ fontSize: 9, textAlign: 'center', bgcolor: formatDate(d) === today ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                    {hi === 0 ? WEEKDAYS[di] : ''}
                  </Box>
                </Tooltip>
              ))
            ))}

            {/* Employee rows */}
            {employees.map(([empId, emp]) => (
              <React.Fragment key={empId}>
                <Box sx={{ p: 0.5, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={emp.name}>
                  {emp.name}
                </Box>
                {weekDates.map((d, di) => (
                  HOURS.map((h, hi) => {
                    const absence = getAbsenceForSlot(emp.absences, formatDate(d), h);
                    return (
                      <Tooltip key={`c-${empId}-${di}-${hi}`} title={absence ? `${absence.startTime}-${absence.endTime}: ${absence.note || absence.statusText}` : ''}>
                        <Box sx={{ height: 16, bgcolor: absence ? getSlotColor(absence.status) : 'background.paper', border: '1px solid', borderColor: 'divider' }} />
                      </Tooltip>
                    );
                  })
                ))}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

